import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, Sparkles } from "lucide-react";

/**
 * Editorial specification sheet. Renders a clean two-column key/value table
 * built from a well-known set of Product fields. Skips any field that isn't
 * populated so we don't ship empty rows. Includes a "Show more" toggle for
 * the long description (kept collapsed by default so the page doesn't feel
 * novel-length on first paint).
 */

function labelize(s) {
  return String(s || "").trim();
}

function firstDefined(...vals) {
  for (const v of vals) {
    if (v == null) continue;
    if (typeof v === "string" && !v.trim()) continue;
    return v;
  }
  return null;
}

export default function SpecSheet({ product, activeVariant }) {
  const [longOpen, setLongOpen] = useState(false);

  const rows = [
    { k: "Silhouette", v: labelize(product.silhouette) },
    { k: "Neckline", v: labelize(product.neckline) },
    { k: "Sleeve", v: labelize(firstDefined(product.sleeve_length, product.sleeves)) },
    { k: "Length", v: labelize(firstDefined(product.length, product.hemline)) },
    { k: "Waistline", v: labelize(product.waistline) },
    { k: "Back", v: labelize(product.back_style) },
    { k: "Fabric", v: labelize(firstDefined(product.fabric, product.material)) },
    { k: "Occasion", v: labelize(product.occasion && (product.occasion[0].toUpperCase() + product.occasion.slice(1))) },
    { k: "Designer", v: labelize(product.designer) },
  ].filter((r) => r.v);

  // "Colors available" and "Sizes available" are meta-rows built from the variants
  const colorsAvailable = (product.variants || []).map((v) => v.color).filter(Boolean);
  const sizesAvailable = (() => {
    if (product.sizes?.length) return [...product.sizes];
    const set = new Set();
    (product.variants || []).forEach((v) => (v.sizes || []).forEach((s) => set.add(s)));
    (product.fits || []).forEach((f) => (f.sizes || []).forEach((s) => set.add(s)));
    return [...set];
  })();

  // SKU with color suffix if a variant is active
  const sku = product.sku
    ? activeVariant?.color
      ? `${product.sku}-${activeVariant.color.replace(/\W+/g, "").toUpperCase().slice(0, 8)}`
      : product.sku
    : null;

  // Prefer the per-variant long description written by the approved xlsx
  // import so the shopper reads copy specific to the color they're viewing.
  // Fall back to the product-level long (mirrored from rank-1), then the
  // legacy `description` field if that's all we've got.
  const longDesc = String(
    activeVariant?.long_description
    || product.long_description
    || product.description
    || ""
  ).trim();
  const showLongToggle = longDesc.length > 240;

  return (
    <div data-testid="spec-sheet" className="mt-10 border-t border-cream pt-8">
      <div className="flex items-baseline justify-between mb-6">
        <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> THE DETAILS
        </p>
        {sku && <p className="font-label text-[9px] tracking-widest text-ink/40">SKU · {sku}</p>}
      </div>

      {/* Spec table */}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        {rows.map((r, i) => (
          <motion.div
            key={r.k}
            initial={{ opacity: 0, y: 4 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.03, duration: 0.35 }}
            data-testid={`spec-${r.k.toLowerCase()}`}
            className="flex items-baseline justify-between gap-4 border-b border-cream pb-3"
          >
            <dt className="font-label text-[10px] tracking-widest text-ink/50 shrink-0">{r.k.toUpperCase()}</dt>
            <dd className="font-body text-sm text-ink text-right">{r.v}</dd>
          </motion.div>
        ))}
      </dl>

      {/* Available colors + sizes rail */}
      {(colorsAvailable.length > 0 || sizesAvailable.length > 0) && (
        <div className="mt-8 border-t border-cream pt-6 space-y-4">
          {colorsAvailable.length > 0 && (
            <div>
              <p className="font-label text-[10px] tracking-widest text-ink/50 mb-2">COLORWAYS · {colorsAvailable.length}</p>
              <p className="font-body text-sm text-ink/80">{colorsAvailable.join(" · ")}</p>
            </div>
          )}
          {sizesAvailable.length > 0 && (
            <div>
              <p className="font-label text-[10px] tracking-widest text-ink/50 mb-2">SIZE RANGE</p>
              <p className="font-body text-sm text-ink/80">{sizesAvailable.join(" · ")}</p>
            </div>
          )}
        </div>
      )}

      {/* Fit notes */}
      {product.fit_notes && (
        <div className="mt-8 border-t border-cream pt-6">
          <p className="font-label text-[10px] tracking-widest text-ink/50 mb-2">FIT NOTES</p>
          <p className="font-body text-sm text-ink/75 leading-relaxed">{product.fit_notes}</p>
        </div>
      )}

      {/* Long description */}
      {longDesc && (
        <div className="mt-8 border-t border-cream pt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="font-label text-[10px] tracking-widest text-ink/50">DESIGNER STORY</p>
            {showLongToggle && (
              <button
                type="button"
                onClick={() => setLongOpen((x) => !x)}
                data-testid="long-desc-toggle"
                className="inline-flex items-center gap-1 font-label text-[10px] tracking-widest text-rosemetal hover:text-ink"
              >
                {longOpen ? <>COLLAPSE <ChevronUp className="w-3 h-3" /></> : <>READ MORE <ChevronDown className="w-3 h-3" /></>}
              </button>
            )}
          </div>
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={longOpen ? "open" : "closed"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="font-body text-[15px] text-ink/75 leading-[1.75] whitespace-pre-line"
            >
              {longOpen || !showLongToggle ? longDesc : longDesc.slice(0, 240).trim() + "…"}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export { SpecSheet };
